import { useCallback, useEffect, useRef, useState } from 'react';
import { B, radius } from '../constants/brand';
import { api } from '../api/client';
import { Icon, useToast } from '../components/ui';
import { NoteBodyEditor } from '../components/shared/NoteBodyEditor';
import { useQuickNoteContext, isQuickNoteShortcut } from './quickNoteContext';
import { NoteLinkBar } from './LinkPicker';

// =============================================================
// QuickNote — jot a note from anywhere in the app (iOS Quick Note, done the
// web way): the header button or Ctrl/⌘ + Alt + N opens a small panel over
// whatever page you are on.
//
//   * The note is created on the FIRST keystroke, with an id this panel chose
//     (POST /api/notebook accepts one, so a retried create cannot make two),
//     and then autosaved. Closing the panel loses nothing.
//   * It is PRIVATE unless you tag it: "+ Tag deal" links it to any deals,
//     capital items or tasks you pick, and the record on screen (a deal or a
//     task — quickNoteContext.js) is OFFERED as a one-click tag, never linked
//     automatically, because linking shares the note with that record's team.
//   * "Open in Notes" takes you to the full Notes app on the same note.
// =============================================================

const SAVE_DELAY_MS = 800;
const newId = () => (crypto.randomUUID ? crypto.randomUUID() : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
  const r = (Math.random() * 16) | 0; return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
}));


export default function QuickNote({ onOpenInNotes }) {
  const { toast } = useToast();
  const ctx = useQuickNoteContext();
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(0);

  useEffect(() => {
    const onKey = (e) => { if (isQuickNoteShortcut(e)) { e.preventDefault(); setOpen(true); setSession(s => s + 1); } };
    const onOpen = () => { setOpen(true); setSession(s => s + 1); };
    window.addEventListener('keydown', onKey);
    window.addEventListener('harvus-quick-note', onOpen);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('harvus-quick-note', onOpen); };
  }, []);

  if (!open) return null;
  return <QuickNotePanel key={session} ctx={ctx} toast={toast} onClose={() => setOpen(false)} onOpenInNotes={onOpenInNotes} />;
}

// The create-then-autosave half of a quick note, shared by this pop-up and the
// Dashboard's Quick note card (components/dashboard/QuickNoteWidget.jsx) so the
// two cannot save differently. `getLinks()` is read only when the note is
// created (the pop-up's "Link to …" tick); omit it for a private note.
export function useQuickNoteDraft({ toast, getLinks } = {}) {
  const [noteId] = useState(newId);
  const [created, setCreated] = useState(false);
  const [state, setState] = useState('empty'); // empty | saving | saved | error
  const pending = useRef(null);
  const base = useRef(null);
  const timer = useRef(null);
  const creating = useRef(null);
  const linksRef = useRef(getLinks);
  linksRef.current = getLinks;

  const save = useCallback(async () => {
    const content = pending.current;
    if (content == null) return;
    setState('saving');
    try {
      if (!created && !creating.current) {
        creating.current = api.post('notebook', {
          id: noteId, content, links: (linksRef.current && linksRef.current()) || [],
        });
        const res = await creating.current;
        base.current = res.modifiedUTC || null;
        setCreated(true);
      } else {
        await creating.current;
        const res = await api.put(`notebook/${noteId}`, { content, baseModifiedUTC: null });
        base.current = res.modifiedUTC;
      }
      if (pending.current === content) pending.current = null;
      setState('saved');
    } catch (e) {
      creating.current = null;
      setState('error');
      if (toast) toast(e.message || 'Could not save the note.', 'error');
    }
  }, [created, noteId, toast]);

  // Unmounting (closing the pop-up, "New note" on the card) flushes the edit.
  useEffect(() => () => { clearTimeout(timer.current); if (pending.current != null) save(); }, [save]);

  const onChange = ({ content, isEmpty }) => {
    if (isEmpty && !created) return;
    pending.current = content;
    clearTimeout(timer.current);
    timer.current = setTimeout(save, SAVE_DELAY_MS);
  };

  // The in-flight create, so a tag added while it is on the wire lands after it.
  const whenCreated = () => creating.current;

  return { noteId, created, state, onChange, whenCreated };
}

// The tags on a quick note. Before the note exists they are held here and sent
// with the create (getLinks); afterwards each change is one PUT …/links and the
// server's answer replaces the list (it carries the labels).
export function useQuickNoteLinks({ toast }) {
  const [links, setLinks] = useState([]);
  const [busy, setBusy] = useState(false);
  const linksRef = useRef(links);
  linksRef.current = links;
  const draftRef = useRef(null);

  const getLinks = () => linksRef.current.map(l => ({ type: l.type, id: l.id }));
  const same = (a, b) => a.type === b.type && String(a.id).toLowerCase() === String(b.id).toLowerCase();

  const change = async (body, optimistic) => {
    const draft = draftRef.current;
    const pendingCreate = draft && draft.whenCreated();
    if (!draft || (!draft.created && !pendingCreate)) { setLinks(optimistic); return; }
    const prev = linksRef.current;
    setLinks(optimistic);
    setBusy(true);
    try {
      if (pendingCreate) await pendingCreate;
      const res = await api.put(`notebook/${draft.noteId}/links`, body);
      if (Array.isArray(res.links)) setLinks(res.links);
    } catch (e) {
      setLinks(prev);
      if (toast) toast(e.message || 'Could not change the tags.', 'error');
    } finally { setBusy(false); }
  };

  const add = (ref) => {
    if (linksRef.current.some(l => same(l, ref))) return;
    change({ add: [{ type: ref.type, id: ref.id }] }, [...linksRef.current, ref]);
  };
  const remove = (ref) => {
    const next = linksRef.current.filter(l => !same(l, ref));
    if (next.length === 0 && draftRef.current?.created
      && !window.confirm('Remove the last tag? The note becomes private to you and disappears from that record.')) return;
    change({ remove: [{ type: ref.type, id: ref.id }] }, next);
  };
  const bind = (draft) => { draftRef.current = draft; };

  return { links, busy, add, remove, getLinks, bind, has: (ref) => linksRef.current.some(l => same(l, ref)) };
}

function QuickNotePanel({ ctx, toast, onClose, onOpenInNotes }) {
  const [linkTarget] = useState(ctx); // the record on screen when the panel opened
  const tags = useQuickNoteLinks({ toast });
  const draft = useQuickNoteDraft({ toast, getLinks: tags.getLinks });
  tags.bind(draft);
  const { noteId, created, state, onChange } = draft;
  const onScreenTagged = linkTarget && tags.has(linkTarget);

  return (
    <div role="dialog" aria-label="Quick note" style={{
      position: 'fixed', left: 24, bottom: 24, zIndex: 1100, width: 'min(420px, calc(100vw - 48px))',
      background: '#fffdf6', borderRadius: radius.lg || 14, border: `1px solid ${B.border}`,
      boxShadow: '0 16px 40px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column', maxHeight: '70vh',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderBottom: `1px solid ${B.divider}` }}>
        <Icon name="note" size={16} color={B.amber || '#c99700'} />
        <strong style={{ fontSize: 13, color: B.text }}>Quick note</strong>
        <span style={{ fontSize: 11, color: state === 'error' ? B.red : B.textLt }}>
          {{ empty: 'Private', saving: 'Saving…', saved: tags.links.length ? 'Saved' : 'Saved · private', error: 'Not saved' }[state]}
        </span>
        <span style={{ flex: 1 }} />
        {created && onOpenInNotes && (
          <button type="button" onClick={() => { onClose(); onOpenInNotes(noteId); }}
            style={{ border: 0, background: 'transparent', color: B.navy, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Open in Notes</button>
        )}
        <button type="button" aria-label="Close quick note" onClick={onClose}
          style={{ border: 0, background: 'transparent', color: B.textMd, cursor: 'pointer', display: 'flex' }}>
          <Icon name="close" size={14} />
        </button>
      </div>
      <div style={{ overflowY: 'auto', padding: '4px 0' }}>
        <NoteBodyEditor onChange={onChange} minHeight={160} autoFocus mathAssist style={{ border: 0, background: 'transparent' }} />
      </div>
      {linkTarget && !onScreenTagged && (
        <button type="button" onClick={() => tags.add(linkTarget)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', border: 0, borderTop: `1px solid ${B.divider}`, background: 'transparent', fontSize: 12, color: B.textMd, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
          + Tag {linkTarget.type === 'Task' ? 'task' : 'deal'} <strong style={{ color: B.text }}>{linkTarget.label || 'on screen'}</strong>
          <span style={{ color: B.textLt }}>— shares it with that {linkTarget.type === 'Task' ? 'task' : 'deal'}'s team</span>
        </button>
      )}
      <NoteLinkBar links={tags.links} busy={tags.busy} onAdd={tags.add} onRemove={tags.remove} />
    </div>
  );
}
